import {logDatadogError} from '#lib/datadog.js';

export const CHUNK_RELOAD_SESSION_KEY = 'gl:chunk-reload';

export type ChunkLoadErrorSource =
  | 'unhandledrejection'
  | 'error'
  | 'react-router-lazy';

const CHUNK_LOAD_ERROR_PATTERN =
  /Loading (?:CSS )?chunk [\da-z]+ failed|Failed to fetch dynamically imported module/i;

function getErrorStringProperty(
  error: Error,
  property: 'code' | 'request' | 'type'
): string | undefined {
  const value = (error as Partial<Record<typeof property, unknown>>)[property];

  return typeof value === 'string' ? value : undefined;
}

export function isChunkLoadError(error: unknown): error is Error {
  if (!(error instanceof Error)) {
    return false;
  }

  return (
    error.name === 'ChunkLoadError' ||
    getErrorStringProperty(error, 'code') === 'CSS_CHUNK_LOAD_FAILED' ||
    CHUNK_LOAD_ERROR_PATTERN.test(error.message)
  );
}

function isChunkReloadBlockedBySessionGuard(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }

  try {
    return sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) === '1';
  } catch {
    return true;
  }
}

async function reportChunkLoadErrorToRum(
  error: Error,
  source: ChunkLoadErrorSource,
  reloadBlockedBySessionGuard: boolean
): Promise<void> {
  await logDatadogError(error, {
    chunk_load_error: true,
    source,
    reload_blocked_by_session_guard: reloadBlockedBySessionGuard,
    reload_attempted: !reloadBlockedBySessionGuard,
    pathname:
      typeof window !== 'undefined' ? window.location.pathname : undefined,
    app_version:
      typeof window !== 'undefined' ? window.GL_DD_VERSION : undefined,
  });
}

export function reloadOnceForChunkLoadError(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  if (isChunkReloadBlockedBySessionGuard()) {
    return false;
  }

  try {
    sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, '1');
  } catch {
    return false;
  }

  window.location.reload();
  return true;
}

export async function handleChunkLoadError(
  error: unknown,
  source: ChunkLoadErrorSource = 'error'
): Promise<boolean> {
  if (!isChunkLoadError(error)) {
    return false;
  }

  const reloadBlockedBySessionGuard = isChunkReloadBlockedBySessionGuard();

  await reportChunkLoadErrorToRum(error, source, reloadBlockedBySessionGuard);

  if (reloadBlockedBySessionGuard) {
    return false;
  }

  return reloadOnceForChunkLoadError();
}

let chunkLoadErrorHandlerInitialized = false;

export function initChunkLoadErrorHandler(): void {
  if (typeof window === 'undefined' || chunkLoadErrorHandlerInitialized) {
    return;
  }

  chunkLoadErrorHandlerInitialized = true;

  window.addEventListener('unhandledrejection', event => {
    if (!isChunkLoadError(event.reason)) {
      return;
    }

    event.preventDefault();
    void handleChunkLoadError(event.reason, 'unhandledrejection');
  });

  window.addEventListener('error', event => {
    if (!isChunkLoadError(event.error)) {
      return;
    }

    event.preventDefault();
    void handleChunkLoadError(event.error, 'error');
  });
}
